import { ArrowRightOutlined } from "@ant-design/icons";
import { Button, Flex, Popover, Typography } from "antd";
import { CSSProperties, Fragment, MouseEvent, ReactNode, useMemo } from "react";
import { hoverCardBodyStyle } from "components/WeeklyCalendar/components/EventHoverCard";
import { measure } from "components/WeeklyCalendar/components/textFit";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import { useInstructorColor } from "context/InstructorColorProvider";
import { textPx } from "shared/constants/zoom";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { clientShortNames } from "shared/utils/clientNames";
import { tzDate } from "shared/utils/dayjs";
import { capacityText } from "./CapacityCell";

type Client = { full: string; first: string };

// The fonts the Klien cell renders in (family from the body, see textFit).
const NAME_FONT = `400 ${textPx(14)}px`;
const HIGHLIGHT_FONT = `600 ${textPx(14)}px`;
const CHIP_FONT = `600 ${textPx(11.5)}px`;
// Padding of .schedule-list-more (index.css), the gap before it, and the
// highlight's own side padding.
const CHIP_PAD = 16;
const CHIP_GAP = 6;
const HIGHLIGHT_PAD = 6;
const SEPARATOR = ", ";

// Names A to Z with their short names (clientShortNames: first names, an
// initial added where two share one). A client the list is filtered on
// moves to the front, so the match can never sit behind "+N".
const orderedClients = (
  schedule: CalendarSchedule,
  highlight: Set<string>
): Client[] => {
  const names = schedule.customer_names ?? [];
  const shortNames = clientShortNames(names, schedule.customer_first_names);
  const clients = names.map((full, i) => ({ full, first: shortNames[i] }));
  clients.sort((a, b) => a.full.localeCompare(b.full, "id"));
  if (highlight.size === 0) return clients;
  return [
    ...clients.filter((c) => highlight.has(c.full)),
    ...clients.filter((c) => !highlight.has(c.full)),
  ];
};

// How many first names fit in `width` with "+N" after them; at least one,
// which then ends in an ellipsis.
const fitCount = (
  clients: Client[],
  width: number,
  highlight: Set<string>
): number => {
  const separator = measure(SEPARATOR, NAME_FONT);
  let used = 0;
  let fits = 0;
  for (let k = 0; k < clients.length; k++) {
    const c = clients[k];
    const marked = highlight.has(c.full);
    used +=
      (k > 0 ? separator : 0) +
      measure(c.first, marked ? HIGHLIGHT_FONT : NAME_FONT) +
      (marked ? HIGHLIGHT_PAD : 0);
    const rest = clients.length - (k + 1);
    const chip = rest
      ? CHIP_GAP + CHIP_PAD + measure(`+${rest}`, CHIP_FONT)
      : 0;
    if (used + chip > width) break;
    fits = k + 1;
  }
  return Math.max(1, fits);
};

const lineStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: CHIP_GAP,
  minWidth: 0,
  whiteSpace: "nowrap",
};
const namesStyle: CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
};
const highlightStyle: CSSProperties = {
  background: colors.navyTint,
  color: colors.navyDark,
  fontWeight: 600,
  borderRadius: 4,
  padding: `0 ${HIGHLIGHT_PAD / 2}px`,
};

/**
 * The Klien cell of Daftar Jadwal: one line at every class size. One client
 * reads in full; from two up, first names A to Z as many as the column holds,
 * then a "+N" chip that opens the whole list. `width` is the column's content
 * width (0 until measured: everything then shows and the line ellipsizes).
 */
export const ClientNamesCell = ({
  schedule,
  width,
  highlight,
  onOpenDetail,
}: {
  schedule: CalendarSchedule;
  width: number;
  highlight: Set<string>;
  onOpenDetail: () => void;
}) => {
  const clients = useMemo(
    () => orderedClients(schedule, highlight),
    [schedule, highlight]
  );
  if (clients.length === 0)
    return <span style={{ color: colors.ink3 }}>-</span>;

  const allNames = clients.map((c) => c.full).join(SEPARATOR);
  if (clients.length === 1) {
    const [only] = clients;
    return (
      <div style={lineStyle}>
        <span style={namesStyle} title={only.full}>
          <span style={highlight.has(only.full) ? highlightStyle : undefined}>
            {only.full}
          </span>
        </span>
      </div>
    );
  }

  const shown =
    width > 0 ? fitCount(clients, width, highlight) : clients.length;
  const rest = clients.length - shown;
  return (
    <div style={lineStyle}>
      <span style={namesStyle} title={allNames}>
        {clients.slice(0, shown).map((c, i) => (
          <Fragment key={`${c.full}-${i}`}>
            {i > 0 && SEPARATOR}
            <span style={highlight.has(c.full) ? highlightStyle : undefined}>
              {c.first}
            </span>
          </Fragment>
        ))}
      </span>
      {rest > 0 && (
        <ClientListPopover
          schedule={schedule}
          clients={clients}
          highlight={highlight}
          onOpenDetail={onOpenDetail}
        >
          <button
            type="button"
            className="schedule-list-more"
            aria-label={translateWithVars("schedule.list_table.more_aria", {
              n: rest,
            })}
            // The row itself opens Detail Jadwal.
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            +{rest}
          </button>
        </ClientListPopover>
      )}
    </div>
  );
};

// The list scrolls inside a capped box; the columns are on the list itself,
// which has no height of its own, or they would spill sideways instead.
const scrollStyle: CSSProperties = {
  maxHeight: 240,
  overflowY: "auto",
  overscrollBehavior: "contain",
};
const listStyle: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  fontSize: textPx(13),
  color: colors.ink,
};
const indexStyle: CSSProperties = {
  display: "inline-block",
  width: 22,
  color: colors.ink3,
  fontSize: textPx(11.5),
  fontVariantNumeric: "tabular-nums",
};

// Every client of the class, opened from "+N". Click only: hover alone is
// not a way in (and the row under it navigates on click).
const ClientListPopover = ({
  schedule,
  clients,
  highlight,
  onOpenDetail,
  children,
}: {
  schedule: CalendarSchedule;
  clients: Client[];
  highlight: Set<string>;
  onOpenDetail: () => void;
  children: ReactNode;
}) => {
  const { getInstructorColorById } = useInstructorColor();
  const dot =
    getInstructorColorById(schedule.instructor_id ?? schedule.staff_id)
      ?.accent ?? colors.ink3;
  const time = `${tzDate(schedule.start_time).format("HH:mm")} - ${tzDate(
    schedule.end_time
  ).format("HH:mm")}`;
  const capacity = capacityText(schedule);
  const meta = [
    translateWithVars("schedule.grid.participants_n", { n: clients.length }),
    capacity &&
      `${translate("schedule.list_table.capacity")} ${capacity.seats}`,
    capacity?.waitlist
      ? `${capacity.waitlist} ${translate("schedule.grid.waitlist")}`
      : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  const content = (
    // React events bubble out of the portal to the row, which navigates.
    <div onClick={(e) => e.stopPropagation()}>
      <Flex align="center" gap={8}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: dot,
            flexShrink: 0,
          }}
        />
        <Typography.Text
          strong
          style={{
            fontFamily: "var(--font-display)",
            fontSize: textPx(14),
            letterSpacing: "-0.01em",
          }}
        >
          {schedule.class_name}
        </Typography.Text>
      </Flex>
      <div style={{ fontSize: textPx(12.5), color: colors.ink2, marginTop: 2 }}>
        {time} · {schedule.instructor_name}
      </div>
      <div
        style={{
          fontSize: textPx(11),
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: colors.ink2,
          margin: "12px 0 6px",
        }}
      >
        {meta}
      </div>
      <div style={scrollStyle}>
        <ol
          style={{
            ...listStyle,
            columnCount: clients.length > 6 ? 2 : 1,
            columnGap: 16,
          }}
        >
          {clients.map((c, i) => (
            <li
              key={`${c.full}-${i}`}
              style={{
                breakInside: "avoid",
                padding: "2px 0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={c.full}
            >
              <span style={indexStyle}>{i + 1}</span>
              <span style={highlight.has(c.full) ? highlightStyle : undefined}>
                {c.full}
              </span>
            </li>
          ))}
        </ol>
      </div>
      <Button
        type="link"
        size="small"
        onClick={onOpenDetail}
        style={{ padding: 0, marginTop: 10, height: "auto" }}
      >
        {translate("title.schedule_detail")} <ArrowRightOutlined />
      </Button>
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      styles={{
        body: {
          ...hoverCardBodyStyle,
          width: clients.length > 6 ? 340 : 260,
        },
      }}
      content={content}
    >
      {children}
    </Popover>
  );
};
